// Opening a term always shows the same thing, wherever it was opened from:
// the full funnel, the products behind it, and the classification control.
import React, { useEffect, useState } from 'react';
import s from '../SearchTerms.module.css';
import { CLASS_LABEL, money, num, pct } from '../format';
import { getTerm, reclassify } from '../api';
import Diagnosis from './Diagnosis';
import Trend from './Trend';

const STAGES = [
  ['impressions', 'Impressions'], ['clicks', 'Clicks'],
  ['cart_adds', 'Cart adds'], ['purchases', 'Purchases'],
];

export default function TermDetail({ token, term, period, startDate, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => getTerm(token, term, { period, start_date: startDate }).then((r) => r.term);

  useEffect(() => {
    let live = true;
    setData(null);
    load().then((d) => live && setData(d)).catch((e) => live && setError(e.message));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, term, period, startDate]);

  // Correction is immediate and visible: a seller who spots their own brand in
  // the wrong bucket and cannot fix it discounts the whole ratio.
  const setLabel = async (label) => {
    setSaving(true);
    try {
      await reclassify(token, term, label);
      setData(await load());
      onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className={s.backdrop} onClick={onClose} />
      <aside className={s.drawer}>
        <div className={s.drawerHead}>
          <button className={s.close} onClick={onClose}>×</button>
          <h2 className={s.drawerTitle}>{term}</h2>
          {data && (
            <p className={s.hint}>
              {num(data.volume)} searches · {data.product_count} of your product
              {data.product_count === 1 ? '' : 's'} appear · #{data.amazon_score} on your best-ranking product
            </p>
          )}
        </div>

        <div className={s.drawerBody}>
          {error && <div className={s.error}>{error}</div>}
          {!data && !error && <div className={s.empty}>Loading…</div>}

          {data && (
            <>
              <Diagnosis term={data} />

              <div className={s.sectionTitle} style={{ marginTop: 22 }}>
                Your funnel against the market
              </div>
              <div className={s.funnel}>
                {STAGES.map(([key, label]) => (
                  <div key={key} className={s.funnelStage}>
                    <div className={s.funnelLabel}>{label}</div>
                    <div className={s.funnelOwn}>{num(data.own[key])}</div>
                    <div className={s.funnelShare}>{pct(data.shares[key])} share</div>
                    <div className={s.funnelMarket}>of {num(data.market[key])}</div>
                  </div>
                ))}
              </div>

              <div className={s.stats}>
                <div className={s.stat}>
                  <span className={s.statValue}>{pct(data.rates.click_rate)}</span>
                  <span className={s.statLabel}>Your click rate</span>
                </div>
                <div className={s.stat}>
                  <span className={s.statValue}>{pct(data.rates.conversion)}</span>
                  <span className={s.statLabel}>Click → purchase</span>
                  <span className={s.statDelta}>
                    market {pct(data.rates.market_conversion)}
                    {!data.rates.rankable && ' · too few clicks to rank'}
                  </span>
                </div>
              </div>

              <div className={s.section}>
                <div className={s.sectionTitle}>Classification</div>
                <div className={s.controls}>
                  {['branded', 'competitor', 'generic'].map((label) => (
                    <button
                      key={label}
                      className={data.classification === label ? s.button : s.secondary}
                      disabled={saving}
                      onClick={() => setLabel(label)}
                    >
                      {CLASS_LABEL[label]}
                    </button>
                  ))}
                  <button className={s.link} disabled={saving} onClick={() => setLabel(null)}>
                    Reset to automatic
                  </button>
                </div>
                <p className={s.hint}>
                  {data.matched_brand
                    ? `Matched automatically on "${data.matched_brand}". Your correction persists across periods.`
                    : 'No brand matched, or set by you. Corrections persist across periods.'}
                </p>
              </div>

              <div className={s.section}>
                <div className={s.sectionTitle}>
                  Your products on this term ({data.product_count})
                </div>
                <div className={s.tableWrap}>
                  <table className={s.table}>
                    <thead>
                      <tr>
                        <th>ASIN</th>
                        <th className={s.numeric}>Impressions</th>
                        <th className={s.numeric}>Clicks</th>
                        <th className={s.numeric}>Purchases</th>
                        <th className={s.numeric}>Median price</th>
                        <th className={s.numeric}>Share of our clicks</th>
                    <th className={s.numeric} title={'Where this term sits among that '
                      + "product's own terms — 1 is its most important"}>Rank on it</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.products || []).map((p) => (
                        <tr key={p.asin}>
                          <td className={s.asin}>{p.asin}</td>
                          <td className={s.numeric}>{num(p.impressions)}</td>
                          <td className={s.numeric}>{num(p.clicks)}</td>
                          <td className={s.numeric}>{num(p.purchases)}</td>
                          <td className={s.numeric}>{money(p.median_price, p.currency)}</td>
                          <td className={s.numeric}>{pct(p.share_of_our_clicks)}</td>
                    <td className={s.numeric}>
                      {p.term_rank ? `#${p.term_rank}` : <span className={s.muted}>—</span>}
                    </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className={s.hint}>
                  This is where a catalogue-level term becomes a product-level action.
                </p>
              </div>

              <Trend token={token} term={term} period={period} startDate={startDate} />
            </>
          )}
        </div>
      </aside>
    </>
  );
}
